import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useAsyncOperation } from "Admin/js/Composables/useAsyncOperation";
import utilities from "Admin/js/Helpers/utilities";

export const useAuthorityLevelStore = defineStore("authority_level", () => {
    const { manageAsyncOperation } = useAsyncOperation();

    const authorityLevels = ref([]);
    const isLoadingData = ref(false);
    const selectedProfileId = ref(null);
    const reportsToProfileId = ref(null);
    const designations = ref([]);

    /** `null` = create mode; row snapshot = edit mode */
    const authorityLevelDrawerSourceRow = ref(null);

    const isAuthorityLevelDrawerEditMode = computed(
        () => authorityLevelDrawerSourceRow.value != null,
    );

    function prepareAuthorityLevelDrawerCreate() {
        authorityLevelDrawerSourceRow.value = null;
    }

    function prepareAuthorityLevelDrawerEdit(row) {
        authorityLevelDrawerSourceRow.value = { ...row };
    }

    function clearAuthorityLevelDrawerContext() {
        authorityLevelDrawerSourceRow.value = null;
    }

    function setSelectedProfileId(profileId) {
        selectedProfileId.value = profileId;
        reportsToProfileId.value = null;
    }

    function setReportsToProfileId(profileId) {
        reportsToProfileId.value = profileId;
    }

    function onError(message) {
        return (error) => {
            const serverMessage = error?.response?.data?.message;
            utilities().showErrorSnackbar(serverMessage ?? message);
        };
    }

    function fetchAllAuthorityLevels() {
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.get("/authority-levels");
                authorityLevels.value = data?.data ?? [];
            },
            onError("Failed to load Seniority levels."),
            (loading) => (isLoadingData.value = loading),
        );
    }

    function createAuthorityLevel(payload) {
        return manageAsyncOperation(async () => {
            const { data } = await axios.post("/authority-levels", payload);
            const created = data?.data ?? {};
            if (created) {
                authorityLevels.value.push(created);
            }
            return created;
        }, onError("Failed to create authority level."));
    }

    function updateAuthorityLevel(authorityLevelId, payload) {
        return manageAsyncOperation(async () => {
            const { data } = await axios.patch(
                `/authority-levels/${authorityLevelId}`,
                payload,
            );
            const updated = data?.data ?? {};
            if (updated) {
                const index = authorityLevels.value.findIndex(
                    (item) => item.id === authorityLevelId,
                );
                if (index !== -1) {
                    authorityLevels.value[index] = updated;
                }
            }
            return updated;
        }, onError("Failed to update authority level."));
    }

    function deleteAuthorityLevel(authorityLevelId) {
        return manageAsyncOperation(async () => {
            await axios.delete(`/authority-levels/${authorityLevelId}`);
            authorityLevels.value = authorityLevels.value.filter(
                (item) => item.id !== authorityLevelId,
            );
            return true;
        }, onError("Failed to delete authority level."));
    }

    /**
     * Called from LiveSync (`tasks.{org}`) when another user creates a seniority level.
     */
    function applyRemoteAuthorityLevelCreated(authorityLevel) {
        if (!authorityLevel?.id) {
            return;
        }
        const index = authorityLevels.value.findIndex(
            (item) => item.id === authorityLevel.id,
        );
        if (index === -1) {
            authorityLevels.value.push(authorityLevel);
        } else {
            authorityLevels.value[index] = authorityLevel;
        }
    }

    /**
     * Called from LiveSync when another user updates a seniority level.
     */
    function applyRemoteAuthorityLevelUpdated(authorityLevel) {
        if (!authorityLevel?.id) {
            return;
        }
        const index = authorityLevels.value.findIndex(
            (item) => item.id === authorityLevel.id,
        );
        if (index !== -1) {
            authorityLevels.value[index] = authorityLevel;
        } else {
            authorityLevels.value.push(authorityLevel);
        }
        if (authorityLevelDrawerSourceRow.value?.id === authorityLevel.id) {
            authorityLevelDrawerSourceRow.value = { ...authorityLevel };
        }
    }

    /**
     * Called from LiveSync when another user deletes one or more seniority levels.
     *
     * @param {number[]} authorityLevelIds
     */
    function applyRemoteAuthorityLevelDeleted(authorityLevelIds) {
        if (!Array.isArray(authorityLevelIds) || authorityLevelIds.length === 0) {
            return;
        }
        const idSet = new Set(authorityLevelIds);
        authorityLevels.value = authorityLevels.value.filter(
            (item) => !idSet.has(item.id),
        );
        if (
            authorityLevelDrawerSourceRow.value?.id != null &&
            idSet.has(authorityLevelDrawerSourceRow.value.id)
        ) {
            clearAuthorityLevelDrawerContext();
        }
    }

    function fetchAllDesignations() {
        return manageAsyncOperation(async () => {
            const { data } = await axios.get("/designations");
            designations.value = data?.data ?? [];
        }, onError("Failed to load designations."));
    }

    return {
        authorityLevels,
        isLoadingData,
        selectedProfileId,
        reportsToProfileId,
        authorityLevelDrawerSourceRow,
        isAuthorityLevelDrawerEditMode,
        designations,

        setSelectedProfileId,
        setReportsToProfileId,
        prepareAuthorityLevelDrawerCreate,
        prepareAuthorityLevelDrawerEdit,
        clearAuthorityLevelDrawerContext,
        fetchAllAuthorityLevels,
        createAuthorityLevel,
        updateAuthorityLevel,
        deleteAuthorityLevel,
        applyRemoteAuthorityLevelCreated,
        applyRemoteAuthorityLevelUpdated,
        applyRemoteAuthorityLevelDeleted,
        fetchAllDesignations,
    };
});
